import { computed, inject, reactive, ref } from 'vue'
import moment from 'moment'

import PaginationComponent from '@/components/_shared/_partials/pagination/pagination.vue'
import FilterSelectComponent from '@/components/_shared/_partials/filter/filter.vue'
import FormInputComponent from '@/components/_shared/_partials/form-input/form-input.vue'
import Tooltip from '@/components/content/views/tooltip/tooltip.vue';
import Card from '@/components/content/views/card/card.vue';
import SkeletonLoader from '@/components/content/views/skeleton-loader/skeleton-loader.vue';
import Modal from '@/components/content/views/modal/modal.vue';
import Discount from '@/components/content/types/discount/discount.vue';
import Tutorial from '@/components/content/types/tutorial/tutorial.vue';
import Webcast from '@/components/content/types/webcast/webcast.vue';
import Article from '@/components/content/types/article/article.vue';
import Course from '@/components/content/types/course/course.vue';
import Magazine from '@/components/content/types/magazine/magazine.vue';

import { library } from '@fortawesome/fontawesome-svg-core'
import { faArchive, faBookmark, faInfo } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import Vibrant from 'node-vibrant'

library.add(faBookmark)
library.add(faArchive)
library.add(faInfo)
/**
 * Content Search View
 * @module Content:Search
 *
 * @prop { Config } config Component configuration
 * @prop { User } user Authenticated user details
 * @prop { String } type Content type filter
 *
 *
 * @example
 koc("loadComponent", {
    name: "scontent-search-view",
    el: "#kelbyone-component-view",
    props: {
      config: { ... },
      user: { ... },
      type: ...
    }
  });
 *
 */

const win = window

export default {
  name: 'ContentSearchView',
  props: {
    config: {
      type: Object,
      required: false,
      default: null,
    },
    user: {
      type: Object,
      required: false,
      default: null,
    },
    type: {
      type: String,
      required: false,
      default: null,
    },
  },
  components: {
    FontAwesomeIcon,
    PaginationComponent,
    FilterSelectComponent,
    FormInputComponent,
    Tooltip,
    Card,
    SkeletonLoader,
    Modal,
    Discount,
    Tutorial,
    Webcast,
    Course,
    Magazine,
    Article,
  },
  setup (props) {
    let config = inject('config')
    config = { ...config, ...props.config }

    const authProvider = inject('authProvider')
    const memberProvider = inject('memberProvider')
    const searchProvider = inject('searchProvider')

    let jwpScript = document.createElement('script')
    jwpScript.setAttribute('src',
      'https://cdn.jwplayer.com/libraries/XLfxzvnU.js')
    document.head.appendChild(jwpScript)

    window.addEventListener('popstate', (event) => {
      searchProvider().loadResults(window.location.search.replace('?', ''),
        config, state.user)
    })

    const search = ref(null);

    const state = reactive({
      term: ref(null),
      loading: ref(true),
      user: computed(() => authProvider().getUser),
      hasUser: computed(() => authProvider().hasUser),
      hasResults: computed(() => searchProvider().hasResults),
      results: computed(() => searchProvider().getResults),
      hasPagination: computed(() => searchProvider().hasPagination),
      pagination: computed(() => searchProvider().getPagination),
      availableFilters: computed(() => searchProvider().getAvailableFilters),
      activeFilters: computed(() => searchProvider().getActiveFilters),
      sortByFilter: computed(() => searchProvider().sortByFilter),
      skeletonCount: ref(12),
      modalItem: {},
      showModal: false,
      activeType: 'course',
      xcoord: 0,
      ycoord: 0,
    })

    const handleResponse = async (response) => {
      if (response.status === 'error' || response.success === false) {
        state.showNotification = true
        state.notification = response.data.message ||
          response.data.data.message || response.data.data
      }
      else {
        state.showNotification = false
        state.notification = null
      }
      state.loading = false
    }

    const loadUser = async (user) => {
      const {
        id,
        token,
      } = user
      let response = await authProvider().loadUser(id, token, config)
      await handleResponse(response)
    }

    const updateTerm = async (term) => {
      getSkeletonCount();
      state.loading = true
      const decoded = decodeURIComponent(window.location.search)
      const query = decoded.replace('?', '')
      let qs = ''
      // check if page is the first parameter in the query string
      if (query.startsWith('page=')) {
        const regex = '[^_page=\\d{1,}'
        const exists = query.match(regex)
        if (exists) {
          qs = query.replace(exists[0], 'page=1')
        }
        else {
          qs = query
        }
      }
      else {
        const regex = '[&]page=\\d{1,}'
        const exists = query.match(regex)
        if (exists) {
          qs = query.replace(exists[0], '&page=1')
        }
        else {
          qs = query
        }
      }

      const parts = qs.split('&')

      if (!term) {
        for (const part of parts) {
          if (part.startsWith('filter[term]')) {
            return state.term = part.split('=')[1]
          }
        }
        return
      }
      let string = ''
      for (const part of parts) {
        if (!part.startsWith('filter[term]')) {
          string = string.length ? `${string}&${part}` : `${part}`
        }
      }
      if (term.value) {
        string = string.length
          ? `${string}&filter[term]=${term.value}`
          : `filter[term]=${term.value}`
      }
      state.term = term.value
      await searchProvider().loadResults(string, config,state.user)
      state.loading = false
    }

    const initialize = async () => {
      state.loading = true
      if (props.user && !state.hasUser) {
        await loadUser(props.user)
      }
      else {
        if (state.hasCookie) {
          return
        }
        const cookie = authProvider().loadUserCookie(config)
        if (cookie) {
          await loadUser(cookie)
        }
      }
      const search = window.location.search.
      replaceAll('%5B', '[').
      replaceAll('%5D', ']')
      window.history.replaceState(search, 'Search - KelbyOne')
      await searchProvider().loadResults(search.replace('?', ''), config, state.user);
      updateTerm()
      state.loading = false
    }

    initialize()

    const toggleBookmark = async (item) => {
      setItemLoading(item, true, 'Updating Bookmark');
      await memberProvider().toggleBookmark(item, state.user.id, !isBookmarked(item), state.user.token, config)
      await loadUser({
        id: state.user.id,
        token: state.user.token,
      });
      setItemLoading(item, false, null);
    }
    const toggleArchive = async (item) => {
      setItemLoading(item, true, "Updating Archive");
      await memberProvider().toggleArchive(item, state.user.id, !isArchived(item), state.user.token, config)
      await loadUser({
        id: state.user.id,
        token: state.user.token,
      })
      setItemLoading(item, false, null);
    }

    const setItemLoading = (itemId, isUpdating, updatingText) => {
      const item = state.results[0].results.find(item => {
        return item.id === itemId;
      });
      item.updatingText = updatingText;
      item.isUpdating = isUpdating;
    }

    const isBookmarked = (item) => {
      return state.user.bookmarks ? state.user.bookmarks.includes(item) : false
    }
    const isArchived = (item) => {
      return state.user.archives ? state.user.archives.includes(item) : false
    }

    const buildCTA = (type) => {
      if (type === 'article') {
        return 'Read Article'
      }
      if (type === 'discount') {
        return 'View Discount'
      }
      return `Watch ${type}`
    }

    const formatDate = (date, type) => {
      if (type === 'course') {
        return moment(String(date)).format('MMMM YYYY')
      }
      return moment(String(date)).format('MMMM D, YYYY')
    }

    const formatSearchUrl = (url) => {
      const parts = url.split('?')
      return `?${parts[1]}`
    }

    const addFilter = (filter, type) => {
      state.params[filter] = type
      // updatePage(1)
      // load()
    }

    const clearFilter = (filter) => {
      delete state.params[filter]
      // load()
    }

    const clear = () => {
      clearFilter('filter[type]')
      reset()
      // load()
    }

    const hasActiveFilters = () => {
      return state.filters && state.filters.length
    }

    const reset = () => {
      getSkeletonCount();
      state.loading = true
      const term = state.params['filter[term]']
        ? state.params['filter[term]']
        : null
      // courses
      delete state.params['filter[instructor]']
      delete state.params['filter[course-category]']
      delete state.params['filter[tier]']
      // lessons
      // discounts
      // tutorials
      delete state.params['filter[tutorialtype]']
      // webcasts
      delete state.params['filter[show]']
      // articles
      delete state.params['filter[article_category]']
      // magazines
      delete state.params['filter[publication]']

      // state.params.page = 1
      delete state.params['page']

      if (term) {
        state.params['filter[term]'] = term
      }
      state.loading = false
      // load()
    }

    const toggleModal = async (item) => {
      if(item) {
        state.modalItem = item;
      } else {
        if(state.modalItem.trailer) {
          jwplayer().remove()
        }
        state.modalItem = {};
      }
      state.showModal = !state.showModal;
    }

    const updatePage = async (page) => {
      getSkeletonCount();
      state.loading = true
      let string = window.location.search.replace('?', '')
      if (string.startsWith('page=')) {
        const regex = '[^_page=\\d{1,}'
        const exists = string.match(regex)
        if (exists) {
          string = string.replace(exists[0], `page=${page}`)
        }
      }
      else {
        const regex = '[&]page=\\d{1,}'
        const exists = string.match(regex)
        if (exists) {
          string = string.replace(exists[0], `&page=${page}`)
        }
        else {
          string = string.length ? `${string}&page=${page}` : `page=${page}`
        }
      }
      await searchProvider().loadResults(string, config, state.user)
      state.loading = false
      window.scrollTo(0,0);
    }

    const filterByType =  async (type) => {
      if(type === state.activeType) return;
      state.activeType = type ? type : 'course';
      getSkeletonCount();
      state.loading = true
      const parts = window.location.search.replace('?', '').split('&')
      let term = null
      for (const part of parts) {
        if (part.startsWith('filter[term]')) {
          term = part
        }
      }
      let string = ''
      if (!type) {
        if (term) {
          string = term
        }
        await searchProvider().loadResults(string, config, state.user)
        state.loading = false
      }
      string = `filter[type]=${type}`
      if (term) {
        string = `${string}&${term}`
      }

      await searchProvider().loadResults(string, config, state.user)
      state.loading = false
    }

    const resetFilters = async () => {
      getSkeletonCount();
      state.loading = true
      const parts = window.location.search.replace('?', '').split('&')
      let term = null
      let type = null
      let string = ''
      for (const part of parts) {
        if (part.startsWith('filter[term]')) {
          term = part
        }
        if (part.startsWith('filter[type]')) {
          type = part
        }
      }
      if (term) {
        string = term
        if (type) {
          string = `${string}&${type}`
        }
      }
      else if (type) {
        string = type
      }

      await searchProvider().loadResults(string, config, state.user)
      state.loading = false
    }

    const apply = async (option) => {
      if (!option) {
        return
      }
      getSkeletonCount()
      state.loading = true
      const parts = option.link.split('?')
      let string = ''
      // check if page is the first parameter in the query string
      if (parts[1].startsWith('page=')) {
        const regex = '[^_page=\\d{1,}'
        const exists = parts[1].match(regex)
        if (exists) {
          string = parts[1].replace(exists[0], 'page=1')
        }
      }
      else {
        const regex = '[&]page=\\d{1,}'
        const exists = parts[1].match(regex)
        if (exists) {
          string = parts[1].replace(exists[0], '&page=1')
        }
      }

      await searchProvider().loadResults(string, config, state.user)
      state.loading = false
    }

    let timer
    const debounce = (term) => {
      clearTimeout(timer)
      return timer = setTimeout(() => {
        return updateTerm(term)
      }, 350)
    }

    const getSkeletonCount = () => {
      if(state.results[0].results.length < 1) {
        state.skeletonCount = 4;
      }
      else if(state.results[0].results.length > 11) {
        state.skeletonCount = 12;
      } else {
        state.skeletonCount = state.results[0].results.length;
      }
    }

    const sortByPlaceholderText = (filter) => {
      let sort = 'Relevancy';
      if( filter.active ) {
        let active = sort = filter.data.find( (item) => item.active );
        sort = active.label;
      }
      return sort;
    }

    return {
      toggleBookmark,
      isBookmarked,
      toggleArchive,
      isArchived,
      apply,
      toggleModal,
      buildCTA,
      formatDate,
      updateTerm,
      filterByType,
      formatSearchUrl,
      updatePage,
      resetFilters,
      hasActiveFilters,
      addFilter,
      clearFilter,
      clear,
      debounce,
      state,
      types: [
        {
          key: 'course',
          label: 'Courses',
        },
        {
          key: 'article',
          label: 'Magazine Articles',
        },
        {
          key: 'webcast',
          label: 'Webcasts',
        },
        {
          key: 'tutorial',
          label: 'Tutorials',
        },
        {
          key: 'discount',
          label: 'Discounts',
        },
      ],
      search,
      sortByPlaceholderText
    }
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    });
    this.state.xcoord = this.search.getBoundingClientRect().left;
    this.state.ycoord = this.search.getBoundingClientRect().top - 100;
  },
  methods: {
    onResize() {
      this.state.xcoord = this.search.getBoundingClientRect().left;
      this.state.ycoord = this.search.getBoundingClientRect().top - 100;
    },
    paginate (page) {
      return this.updatePage(page);
    },
    buildCourseTitle (title) {
      return `
        Course: ${title}`
    },
    formatDuration: (seconds) => {
      seconds = parseInt(seconds)
      if ('undefined' == typeof seconds || Number.isNaN(seconds)) {
        seconds = 0
      }
      var date = new Date(null)
      var start = 12
      var length = 7
      date.setSeconds(seconds)

      let trimmed = date.toISOString().substr(start, length)

      if (trimmed.startsWith('0:')) {
        return trimmed.substr(2, 5)
      }

      return trimmed
    },
    formatUrl: (item) => {
      if ('instructor' === item.type) {
        return `/author/${item.slug}/`;
      }

      // @ToDo add this to the component config
      if (item.type === 'article') {
        return item.url
      }
      const parts = ['type', 'slug']
      let url = '/'
      for (const part of parts) {
        url = `${url}${item[part]}/`
      }
      return url
    },
  },
}
